import type { FloorPlanItem, FloorPlanStair, FloorPlanStairKind } from '../../../types';
import { catalogEntry } from './floorPlanCatalog';

export const STAIR_CATALOG_IDS = ['stair-straight', 'stair-l', 'stair-u', 'stair-spiral'] as const;

export type StairSeg = { x1: number; y1: number; x2: number; y2: number };
export type StairCircle = { cx: number; cy: number; r: number };
export type StairLabel = { x: number; y: number; text: string; font: number };
export type StairDrawing = {
  outline?: string;
  circles: StairCircle[];
  treads: StairSeg[];
  arrows: StairSeg[];
  arrowPath?: string;
  labels: StairLabel[];
};

export function stairKindOf(catalogId: string): FloorPlanStairKind | null {
  if (catalogId === 'stair-straight') return 'straight';
  if (catalogId === 'stair-l') return 'l';
  if (catalogId === 'stair-u') return 'u';
  if (catalogId === 'stair-spiral') return 'spiral';
  return null;
}

export function isStairCatalogId(catalogId: string): boolean {
  return stairKindOf(catalogId) != null;
}

export function defaultStair(kind: FloorPlanStairKind): FloorPlanStair {
  if (kind === 'straight') return { treads: 14, turn: 'right' };
  if (kind === 'spiral') return { treads: 16, turn: 'right' };
  if (kind === 'u') return { treads: 10, landTreads: 10, turn: 'right', runWidthIn: 36 };
  return { treads: 8, landTreads: 8, turn: 'right', runWidthIn: 36 };
}

export function stairOf(item: Pick<FloorPlanItem, 'catalogId' | 'stair'>): FloorPlanStair {
  const kind = stairKindOf(item.catalogId);
  return { ...(kind ? defaultStair(kind) : {}), ...item.stair };
}

export function stairRunWidth(item: Pick<FloorPlanItem, 'catalogId' | 'widthIn' | 'depthIn' | 'stair'>): number {
  const kind = stairKindOf(item.catalogId);
  if (kind === 'straight') return Math.max(18, item.widthIn);
  if (kind === 'spiral') return Math.min(item.widthIn, item.depthIn);
  const max = Math.max(18, Math.min(item.widthIn, item.depthIn) - 8);
  return Math.min(Math.max(stairOf(item).runWidthIn ?? 36, 18), max);
}

export function itemFromCatalog(catalogId: string, x: number, y: number, id: string): FloorPlanItem | null {
  const entry = catalogEntry(catalogId);
  if (!entry) return null;
  const kind = stairKindOf(catalogId);
  return {
    id,
    catalogId,
    x,
    y,
    rotation: 0,
    widthIn: entry.widthIn,
    depthIn: entry.depthIn,
    stair: kind ? defaultStair(kind) : undefined,
  };
}

export function retargetStair(item: FloorPlanItem, catalogId: string): FloorPlanItem {
  const next = itemFromCatalog(catalogId, item.x, item.y, item.id);
  if (!next) return item;
  return {
    ...next,
    rotation: item.rotation,
    stair: { ...next.stair, turn: stairOf(item).turn },
  };
}

function mirrorX(x: number, w: number, flip: boolean): number {
  return flip ? w - x : x;
}

function seg(x1: number, y1: number, x2: number, y2: number): StairSeg {
  return { x1, y1, x2, y2 };
}

function arrowHead(x1: number, y1: number, x2: number, y2: number, size: number): StairSeg[] {
  const len = Math.hypot(x2 - x1, y2 - y1);
  if (len < 0.5) return [];
  const ux = (x2 - x1) / len;
  const uy = (y2 - y1) / len;
  const px = -uy;
  const py = ux;
  return [
    seg(x2, y2, x2 - ux * size + px * size * 0.55, y2 - uy * size + py * size * 0.55),
    seg(x2, y2, x2 - ux * size - px * size * 0.55, y2 - uy * size - py * size * 0.55),
  ];
}

function pathFrom(points: Array<{ x: number; y: number }>, closed = true): string {
  if (!points.length) return '';
  const head = `M ${points[0].x} ${points[0].y}`;
  let d = head;
  for (let i = 1; i < points.length; i += 1) d += ` L ${points[i].x} ${points[i].y}`;
  return closed ? `${d} Z` : d;
}

function divide(count: number, start: number, end: number): number[] {
  const n = Math.max(2, Math.round(count));
  const values: number[] = [];
  for (let i = 1; i < n; i += 1) values.push(start + ((end - start) * i) / n);
  return values;
}

function straightDrawing(w: number, d: number, treads: number, flip: boolean): StairDrawing {
  const treadsN = Math.max(3, treads);
  const treadsSegs: StairSeg[] = [];
  divide(treadsN, 0, d).forEach((y) => treadsSegs.push(seg(0, y, w, y)));
  const fromY = flip ? 12 : d - 12;
  const toY = flip ? d - 14 : 14;
  const mid = w / 2;
  const arrows = [seg(mid, fromY, mid, toY), ...arrowHead(mid, fromY, mid, toY, Math.min(10, w * 0.22))];
  return {
    outline: `M 0 0 H ${w} V ${d} H 0 Z`,
    circles: [],
    treads: treadsSegs,
    arrows,
    labels: [{ x: mid, y: toY + (flip ? 10 : -8), text: 'UP', font: Math.min(w, d) * 0.12 }],
  };
}

function lDrawing(w: number, d: number, run: number, treads: number, landTreads: number, flip: boolean): StairDrawing {
  const t = run;
  const pts = [
    { x: mirrorX(0, w, flip), y: d },
    { x: mirrorX(w, w, flip), y: d },
    { x: mirrorX(w, w, flip), y: 0 },
    { x: mirrorX(w - t, w, flip), y: 0 },
    { x: mirrorX(w - t, w, flip), y: d - t },
    { x: mirrorX(0, w, flip), y: d - t },
  ];
  const treadsSegs: StairSeg[] = [];
  divide(treads, 0, w - t).forEach((x) => {
    treadsSegs.push(seg(mirrorX(x, w, flip), d - t, mirrorX(x, w, flip), d));
  });
  divide(landTreads, 0, d - t).forEach((y) => {
    treadsSegs.push(seg(mirrorX(w - t, w, flip), y, mirrorX(w, w, flip), y));
  });
  const a = { x: mirrorX(8, w, flip), y: d - t / 2 };
  const b = { x: mirrorX(w - t / 2, w, flip), y: d - t / 2 };
  const c = { x: mirrorX(w - t / 2, w, flip), y: 12 };
  return {
    outline: pathFrom(pts),
    circles: [],
    treads: treadsSegs,
    arrows: [seg(a.x, a.y, b.x, b.y), seg(b.x, b.y, c.x, c.y), ...arrowHead(b.x, b.y, c.x, c.y, Math.min(10, t * 0.22))],
    labels: [{ x: c.x, y: 22, text: 'UP', font: Math.min(t, 36) * 0.28 }],
  };
}

function uDrawing(w: number, d: number, run: number, treads: number, landTreads: number, flip: boolean): StairDrawing {
  const t = run;
  const pts = [
    { x: mirrorX(0, w, flip), y: t },
    { x: mirrorX(t, w, flip), y: t },
    { x: mirrorX(t, w, flip), y: d },
    { x: mirrorX(w - t, w, flip), y: d },
    { x: mirrorX(w - t, w, flip), y: t },
    { x: mirrorX(w, w, flip), y: t },
    { x: mirrorX(w, w, flip), y: 0 },
    { x: mirrorX(0, w, flip), y: 0 },
  ];
  const treadsSegs: StairSeg[] = [];
  divide(treads, t, d).forEach((y) => {
    treadsSegs.push(seg(mirrorX(0, w, flip), y, mirrorX(t, w, flip), y));
  });
  divide(landTreads, t, d).forEach((y) => {
    treadsSegs.push(seg(mirrorX(w - t, w, flip), y, mirrorX(w, w, flip), y));
  });
  const a = { x: mirrorX(t / 2, w, flip), y: d - 10 };
  const b = { x: mirrorX(t / 2, w, flip), y: t / 2 };
  const c = { x: mirrorX(w - t / 2, w, flip), y: t / 2 };
  const e = { x: mirrorX(w - t / 2, w, flip), y: d - 14 };
  return {
    outline: pathFrom(pts),
    circles: [],
    treads: treadsSegs,
    arrows: [
      seg(a.x, a.y, b.x, b.y),
      seg(b.x, b.y, c.x, c.y),
      seg(c.x, c.y, e.x, e.y),
      ...arrowHead(c.x, c.y, e.x, e.y, Math.min(10, t * 0.22)),
    ],
    labels: [{ x: e.x, y: d - 22, text: 'UP', font: Math.min(t, 36) * 0.28 }],
  };
}

function spiralDrawing(w: number, d: number, treads: number, flip: boolean): StairDrawing {
  const cx = w / 2;
  const cy = d / 2;
  const r = Math.min(w, d) / 2 - 0.4;
  const pole = Math.max(3.5, r * 0.16);
  const n = Math.max(8, treads);
  const start = Math.PI / 2;
  const dir = flip ? -1 : 1;
  const treadsSegs: StairSeg[] = [];
  for (let i = 0; i < n; i += 1) {
    const a = start + dir * ((Math.PI * 2 * i) / n);
    treadsSegs.push(
      seg(cx + Math.cos(a) * pole, cy + Math.sin(a) * pole, cx + Math.cos(a) * r, cy + Math.sin(a) * r),
    );
  }
  const midR = (pole + r) * 0.5;
  const a0 = start + dir * 0.35;
  const a1 = start + dir * 2.15;
  const p0 = { x: cx + Math.cos(a0) * midR, y: cy + Math.sin(a0) * midR };
  const p1 = { x: cx + Math.cos(a1) * midR, y: cy + Math.sin(a1) * midR };
  const large = Math.abs(a1 - a0) > Math.PI ? 1 : 0;
  const sweep = dir > 0 ? 1 : 0;
  const tangent = { x: cx + Math.cos(a1 - dir * 0.12) * midR, y: cy + Math.sin(a1 - dir * 0.12) * midR };
  const entry = { x: cx + Math.cos(start) * (r * 0.78), y: cy + Math.sin(start) * (r * 0.78) };
  return {
    circles: [
      { cx, cy, r },
      { cx, cy, r: pole },
    ],
    treads: treadsSegs,
    arrows: arrowHead(tangent.x, tangent.y, p1.x, p1.y, Math.min(9, r * 0.18)),
    arrowPath: `M ${p0.x} ${p0.y} A ${midR} ${midR} 0 ${large} ${sweep} ${p1.x} ${p1.y}`,
    labels: [{ x: entry.x, y: entry.y + r * 0.18, text: 'UP', font: r * 0.22 }],
  };
}

export function stairDrawing(item: Pick<FloorPlanItem, 'catalogId' | 'widthIn' | 'depthIn' | 'stair'>): StairDrawing | null {
  const kind = stairKindOf(item.catalogId);
  if (!kind) return null;
  const stair = stairOf(item);
  const flip = stair.turn === 'left';
  const treads = stair.treads ?? defaultStair(kind).treads ?? 8;
  const land = stair.landTreads ?? treads;
  const run = stairRunWidth(item);
  if (kind === 'straight') return straightDrawing(item.widthIn, item.depthIn, treads, flip);
  if (kind === 'l') return lDrawing(item.widthIn, item.depthIn, run, treads, land, flip);
  if (kind === 'u') return uDrawing(item.widthIn, item.depthIn, run, treads, land, flip);
  return spiralDrawing(item.widthIn, item.depthIn, treads, flip);
}

export function stairTreadCount(drawing: StairDrawing): number {
  return drawing.treads.length;
}
